import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import useFirestore from '../../hooks/useFirestore';
import useAuth from '../../hooks/useAuth';
import usePagePermissions from '../../hooks/usePagePermissions';
import { db } from '../../firebaseConfig';
import {
    getDocs,
    collection,
    addDoc,
    updateDoc,
    doc,
    getDoc,
    setDoc,
    runTransaction,
    query,
    where,
    orderBy,
    limit
} from 'firebase/firestore';
import Toast from '../../components/common/Toast/Toast';
import DeleteModal from '../../components/common/DeleteModal/DeleteModal';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import Pagination from '../../components/common/Pagination/Pagination';
import TableSkeleton from '../../components/common/TableSkeleton/TableSkeleton';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import ViewOrderModal from '../../components/common/ViewOrderModal/ViewOrderModal';
import ComboBox from '../../components/common/ComboBox/ComboBox';
import PrintModal from '../../components/common/PrintModal/PrintModal';
import styles from './Orders.module.css';
import { useNotifications } from '../../contexts/NotificationContext';

// React Icons
import {
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiPackage,
    FiClipboard,
    FiEye,
    FiPlus,
    FiSend,
    FiThumbsUp,
    FiThumbsDown,
    FiCheckCircle,
    FiChevronDown,
    FiChevronRight,
    FiDollarSign,
    FiUser,
    FiPrinter
} from 'react-icons/fi';
import { FaQuoteRight } from 'react-icons/fa';
import { MdOutlineAttachMoney } from "react-icons/md";

// ============================================
// 1. CONSTANTES E DEFINIÇÕES
// ============================================
const PURCHASE_REQUESTS_COLLECTION = 'purchase-requests';
const PURCHASE_ORDERS_COLLECTION = 'purchase-orders';
const SUPPLIERS_COLLECTION = 'suppliers';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const ITEMS_PER_PAGE = 10;

// Status do Pedido de Compra
const REQUEST_STATUS = {
    DRAFT: 'draft',
    PENDING: 'pending',
    UNDER_REVIEW: 'under_review',
    QUOTING: 'quoting',
    QUOTED: 'quoted',
    APPROVED: 'approved',
    CONVERTED: 'converted',
    REJECTED: 'rejected',
    CANCELLED: 'cancelled',
    PARTIALLY_CONVERTED: 'partially_converted'
};

const REQUEST_STATUS_LABELS = {
    [REQUEST_STATUS.DRAFT]: { label: 'Rascunho', className: 'statusBadgeDraft' },
    [REQUEST_STATUS.PENDING]: { label: 'Pendente', className: 'statusBadgePending' },
    [REQUEST_STATUS.UNDER_REVIEW]: { label: 'Em Análise', className: 'statusBadgeInfo' },
    [REQUEST_STATUS.QUOTING]: { label: 'Em Cotação', className: 'statusBadgeInfo' },
    [REQUEST_STATUS.QUOTED]: { label: 'Cotado', className: 'statusBadgeApproved' },
    [REQUEST_STATUS.APPROVED]: { label: 'Aprovado', className: 'statusBadgeApproved' },
    [REQUEST_STATUS.CONVERTED]: { label: 'Convertido', className: 'statusBadgeCompleted' },
    [REQUEST_STATUS.REJECTED]: { label: 'Rejeitado', className: 'statusBadgeRejected' },
    [REQUEST_STATUS.CANCELLED]: { label: 'Cancelado', className: 'statusBadgeCancelled' },
    [REQUEST_STATUS.PARTIALLY_CONVERTED]: { label: 'Parcialmente Convertido', className: 'statusBadgeInfo' }
};

// Prioridades
const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Baixa', icon: '🟢' },
    { value: 'medium', label: 'Média', icon: '🟡' },
    { value: 'high', label: 'Alta', icon: '🟠' },
    { value: 'urgent', label: 'Urgente', icon: '🔴' }
];

// Tipos de Solicitação
const REQUEST_TYPES = [
    { value: 'material', label: 'Matéria Prima' },
    { value: 'packaging', label: 'Embalagem' },
    { value: 'service', label: 'Serviço' },
    { value: 'equipment', label: 'Equipamento' },
    { value: 'other', label: 'Outro' }
];

// Status da Cotação por Fornecedor
const SUPPLIER_QUOTE_STATUS = {
    PENDING: 'pending',
    QUOTED: 'quoted',
    SELECTED: 'selected',
    APPROVED: 'approved',
    CONVERTED: 'converted',
    REJECTED: 'rejected'
};

// ============================================
// 2. COMPONENTE DE COTAÇÃO POR FORNECEDOR
// ============================================

const SupplierQuotesSection = ({
    requestItems,
    suppliers,
    supplierQuotes,
    onAddQuote,
    onEditQuote,
    onDeleteQuote,
    onSelectQuote,
    onApproveQuote,
    onRejectQuote,
    onOpenRejectModal,
    onConvertQuote,
    isEditing,
    isExpanded,
    onToggleExpand,
    isPrintView = false,
    canConvert = true,
    canApprove = true
}) => {
    const [showQuoteForm, setShowQuoteForm] = useState(false);
    const [editingQuote, setEditingQuote] = useState(null);
    const [selectedSupplierId, setSelectedSupplierId] = useState('');
    const [selectedItemIds, setSelectedItemIds] = useState([]);

    // Get all unique suppliers from request items
    const availableSuppliers = useMemo(() => {
        if (!requestItems || !suppliers) return [];

        const supplierMap = new Map();
        requestItems.forEach(item => {
            (item.suppliers || []).forEach(s => {
                const supplier = suppliers.find(sup => sup.id === s.supplierId);
                if (supplier && !supplierMap.has(supplier.id)) {
                    supplierMap.set(supplier.id, {
                        ...supplier,
                        availableItems: []
                    });
                }
                if (supplier) {
                    const entry = supplierMap.get(supplier.id);
                    if (entry) {
                        entry.availableItems.push({
                            itemId: item.id,
                            itemCode: item.code,
                            itemName: item.name,
                            quantity: item.quantity,
                            unitPrice: item.unitPrice,
                            total: item.total
                        });
                    }
                }
            });
        });
        return Array.from(supplierMap.values());
    }, [requestItems, suppliers]);

    // Get items for selected supplier
    const supplierItems = useMemo(() => {
        if (!selectedSupplierId) return [];
        const supplier = availableSuppliers.find(s => s.id === selectedSupplierId);
        return supplier?.availableItems || [];
    }, [selectedSupplierId, availableSuppliers]);

    // Format currency
    const formatCurrency = (value) => {
        if (!value && value !== 0) return 'R$ 0,00';
        return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    };

    const handleAddQuote = (quoteData) => {
        const generateUniqueId = () => {
            return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        };

        const newQuote = {
            id: generateUniqueId(),
            ...quoteData,
            status: SUPPLIER_QUOTE_STATUS.PENDING,
            createdAt: new Date(),
            items: quoteData.items || [],
            ipi: parseFloat(quoteData.ipi) || 0,
            subtotal: quoteData.subtotal || 0,
            totalValue: quoteData.totalValue || 0
        };
        onAddQuote(newQuote);
        setShowQuoteForm(false);
        setSelectedSupplierId('');
        setSelectedItemIds([]);
    };

    const handleEditQuote = (quote) => {
        setEditingQuote(quote);
        setSelectedSupplierId(quote.supplierId);
        setSelectedItemIds(quote.items.map(item => item.itemId));
        setShowQuoteForm(true);
    };

    const handleCancelQuoteForm = () => {
        setShowQuoteForm(false);
        setEditingQuote(null);
        setSelectedSupplierId('');
        setSelectedItemIds([]);
    };

    const handleToggleItemSelection = (itemId) => {
        setSelectedItemIds(prev =>
            prev.includes(itemId)
                ? prev.filter(id => id !== itemId)
                : [...prev, itemId]
        );
    };

    const getStatusLabel = (status) => {
        const labels = {
            [SUPPLIER_QUOTE_STATUS.PENDING]: 'Pendente',
            [SUPPLIER_QUOTE_STATUS.QUOTED]: 'Cotado',
            [SUPPLIER_QUOTE_STATUS.SELECTED]: 'Selecionado',
            [SUPPLIER_QUOTE_STATUS.APPROVED]: 'Aprovado',
            [SUPPLIER_QUOTE_STATUS.CONVERTED]: 'Convertido',
            [SUPPLIER_QUOTE_STATUS.REJECTED]: 'Rejeitado'
        };
        return labels[status] || status;
    };

    const getStatusClass = (status) => {
        const classes = {
            [SUPPLIER_QUOTE_STATUS.PENDING]: 'statusBadgePending',
            [SUPPLIER_QUOTE_STATUS.QUOTED]: 'statusBadgeInfo',
            [SUPPLIER_QUOTE_STATUS.SELECTED]: 'statusBadgeApproved',
            [SUPPLIER_QUOTE_STATUS.APPROVED]: 'statusBadgeApproved',
            [SUPPLIER_QUOTE_STATUS.CONVERTED]: 'statusBadgeCompleted',
            [SUPPLIER_QUOTE_STATUS.REJECTED]: 'statusBadgeRejected'
        };
        return classes[status] || 'statusBadgeDraft';
    };

    const getSupplierName = (supplierId) => {
        const supplier = suppliers?.find(s => s.id === supplierId);
        return supplier?.razaoSocial || supplierId;
    };

    // Check status counts
    const hasApprovedQuote = supplierQuotes.some(q => q.status === SUPPLIER_QUOTE_STATUS.APPROVED);
    const hasConvertedQuote = supplierQuotes.some(q => q.status === SUPPLIER_QUOTE_STATUS.CONVERTED);

    // Filter available suppliers (exclude those already quoted)
    const quotedSupplierIds = supplierQuotes
        .filter(q => q.status !== SUPPLIER_QUOTE_STATUS.REJECTED && q.status !== SUPPLIER_QUOTE_STATUS.CONVERTED)
        .map(q => q.supplierId);

    const availableSuppliersToQuote = useMemo(() => {
        return availableSuppliers.filter(s => !quotedSupplierIds.includes(s.id));
    }, [availableSuppliers, quotedSupplierIds]);

    return (
        <div className={`${styles.supplierQuotesSection} ${isPrintView ? styles.printSupplierQuotesSection : ''}`}>
            <div
                className={styles.supplierQuotesHeader}
                onClick={!isPrintView ? onToggleExpand : undefined}
                style={{ cursor: !isPrintView ? 'pointer' : 'default' }}
            >
                <div className={styles.supplierQuotesTitle}>
                    <strong>Cotações por Fornecedor</strong>
                    <span className={styles.quoteCount}>{supplierQuotes.length} cotações</span>
                    {hasApprovedQuote && !isPrintView && (
                        <span className={styles.approvedBadge}>
                            <FiCheckCircle size={14} /> {supplierQuotes.filter(q => q.status === SUPPLIER_QUOTE_STATUS.APPROVED).length} aprovada(s)
                        </span>
                    )}
                    {hasConvertedQuote && !isPrintView && (
                        <span className={styles.convertedBadge}>
                            <FiCheck size={14} /> {supplierQuotes.filter(q => q.status === SUPPLIER_QUOTE_STATUS.CONVERTED).length} convertida(s)
                        </span>
                    )}
                    {!isPrintView && (
                        <span className={styles.expandIcon}>
                            {isExpanded ? <FiChevronDown size={18} /> : <FiChevronRight size={18} />}
                        </span>
                    )}
                </div>
            </div>

            {(isExpanded || isPrintView) && (
                <div className={styles.supplierQuotesContent}>
                    {!isPrintView && (
                        <>
                            {/* Formulário para adicionar/editar cotação */}
                            {showQuoteForm ? (
                                <SupplierQuoteForm
                                    availableSuppliers={availableSuppliersToQuote}
                                    supplierItems={supplierItems}
                                    selectedSupplierId={selectedSupplierId}
                                    selectedItemIds={selectedItemIds}
                                    onSupplierChange={setSelectedSupplierId}
                                    onToggleItem={handleToggleItemSelection}
                                    initialData={editingQuote}
                                    onSave={(quoteData) => {
                                        if (editingQuote) {
                                            onEditQuote(editingQuote.id, quoteData);
                                        } else {
                                            handleAddQuote(quoteData);
                                        }
                                        handleCancelQuoteForm();
                                    }}
                                    onCancel={handleCancelQuoteForm}
                                    isLoading={isEditing}
                                    isEditing={!!editingQuote}
                                />
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setShowQuoteForm(true)}
                                    className={styles.addQuoteButton}
                                    disabled={isEditing || availableSuppliersToQuote.length === 0}
                                >
                                    <FiPlus size={16} />
                                    Adicionar Cotação
                                    {availableSuppliersToQuote.length === 0 && (
                                        <span className={styles.hint}>(Nenhum fornecedor disponível)</span>
                                    )}
                                </button>
                            )}

                            {/* Lista de cotações */}
                            <div className={styles.quotesTableWrapper}>
                                {supplierQuotes.length > 0 ? (
                                    <table className={styles.quotesTable}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '50px' }}>#</th>
                                                <th>Fornecedor</th>
                                                <th>Itens</th>
                                                <th className="text-right">Subtotal</th>
                                                <th className="text-center">IPI</th>
                                                <th className="text-right">Valor Total</th>
                                                <th className="text-center">Prazo</th>
                                                <th className="text-center">Pagamento</th>
                                                <th className="text-center">Status</th>
                                                <th className="text-center">Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {supplierQuotes.map((quote, qIndex) => {
                                                const isSelected = quote.status === SUPPLIER_QUOTE_STATUS.SELECTED;
                                                const isApproved = quote.status === SUPPLIER_QUOTE_STATUS.APPROVED;
                                                const isConverted = quote.status === SUPPLIER_QUOTE_STATUS.CONVERTED;
                                                const isRejected = quote.status === SUPPLIER_QUOTE_STATUS.REJECTED;
                                                const isPending = quote.status === SUPPLIER_QUOTE_STATUS.PENDING;
                                                const isQuoted = quote.status === SUPPLIER_QUOTE_STATUS.QUOTED;

                                                // Action rules per quote
                                                const canEdit = !isApproved && !isConverted && !isRejected && isPending;
                                                const canDelete = !isApproved && !isConverted && !isRejected && isPending;
                                                const canSelect = !isSelected && !isApproved && !isConverted && !isRejected && (isQuoted || isPending);
                                                const canApproveAction = (isSelected || isQuoted) && !isApproved && !isConverted && !isRejected && canApprove;
                                                const canReject = !isApproved && !isConverted && !isRejected && (isQuoted || isSelected) && canApprove;
                                                const canConvertAction = isApproved && !isConverted && canConvert;

                                                return (
                                                    <tr key={quote.id} className={isSelected ? styles.selectedQuoteRow : ''}>
                                                        <td className="text-center">{qIndex + 1}</td>
                                                        <td>
                                                            <strong>{getSupplierName(quote.supplierId)}</strong>
                                                            {quote.supplierCode && (
                                                                <span className={styles.supplierCode}>({quote.supplierCode})</span>
                                                            )}
                                                            {isRejected && quote.rejectionReason && (
                                                                <div className={styles.rejectionReason}>
                                                                    <small style={{ color: '#dc3545', display: 'block', marginTop: '4px' }}>
                                                                        Motivo: {quote.rejectionReason}
                                                                    </small>
                                                                </div>
                                                            )}
                                                            {isConverted && quote.convertedToOrderCode && (
                                                                <div className={styles.conversionInfo}>
                                                                    <small style={{ color: '#28a745', display: 'block', marginTop: '4px' }}>
                                                                        Pedido: {quote.convertedToOrderCode}
                                                                    </small>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {quote.items.map(item => (
                                                                <div key={item.itemId} className={styles.quoteItem}>
                                                                    <span>{item.itemCode} - {item.itemName}</span>
                                                                    <span className={styles.quoteItemQty}>x{item.quantity}</span>
                                                                    <span className={styles.quoteItemPrice}>
                                                                        {formatCurrency(item.unitPrice)}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </td>
                                                        <td className="text-right">
                                                            {formatCurrency(quote.subtotal || 0)}
                                                        </td>
                                                        <td className="text-center">
                                                            {quote.ipi ? `${quote.ipi}%` : '-'}
                                                        </td>
                                                        <td className="text-right" style={{ fontWeight: 'bold' }}>
                                                            {formatCurrency(quote.totalValue || 0)}
                                                        </td>
                                                        <td className="text-center">
                                                            {quote.deliveryTime ? `${quote.deliveryTime} dias` : '-'}
                                                        </td>
                                                        <td className="text-center">
                                                            {quote.paymentTerms || '-'}
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={`${styles.quoteStatus} ${styles[getStatusClass(quote.status)]}`}>
                                                                {getStatusLabel(quote.status)}
                                                            </span>
                                                            {isRejected && quote.rejectedBy && (
                                                                <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                                                                    por {quote.rejectedBy}
                                                                </div>
                                                            )}
                                                            {isApproved && quote.approvedBy && (
                                                                <div style={{ fontSize: '11px', color: '#28a745', marginTop: '2px' }}>
                                                                    por {quote.approvedBy}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            <div className={styles.actionsContainer}>
                                                                {/* Select button */}
                                                                {canSelect && (
                                                                    <button
                                                                        onClick={() => onSelectQuote(quote.id)}
                                                                        className={`${styles.actionButton} ${styles.selectButton}`}
                                                                        disabled={isEditing}
                                                                        title="Selecionar cotação"
                                                                    >
                                                                        <FiCheck size={14} />
                                                                    </button>
                                                                )}

                                                                {/* Approve button - individual */}
                                                                {canApproveAction && (
                                                                    <button
                                                                        onClick={() => onApproveQuote(quote.id)}
                                                                        className={`${styles.actionButton} ${styles.approveButton}`}
                                                                        disabled={isEditing}
                                                                        title="Aprovar cotação"
                                                                    >
                                                                        <FiThumbsUp size={14} />
                                                                    </button>
                                                                )}

                                                                {/* Reject button */}
                                                                {canReject && (
                                                                    <button
                                                                        onClick={() => onOpenRejectModal(quote)}
                                                                        className={`${styles.actionButton} ${styles.rejectButton}`}
                                                                        disabled={isEditing}
                                                                        title="Rejeitar cotação"
                                                                    >
                                                                        <FiThumbsDown size={14} />
                                                                    </button>
                                                                )}

                                                                {/* Convert to Purchase Order - individual */}
                                                                {canConvertAction && (
                                                                    <button
                                                                        onClick={() => onConvertQuote(quote.id)}
                                                                        className={`${styles.actionButton} ${styles.convertButton}`}
                                                                        disabled={isEditing}
                                                                        title="Converter em Pedido de Compra"
                                                                    >
                                                                        <FiSend size={14} />
                                                                    </button>
                                                                )}

                                                                {/* Edit button */}
                                                                {canEdit && (
                                                                    <button
                                                                        onClick={() => handleEditQuote(quote)}
                                                                        className={`${styles.actionButton} ${styles.editButton}`}
                                                                        disabled={isEditing}
                                                                        title="Editar"
                                                                    >
                                                                        <FiEdit size={14} />
                                                                    </button>
                                                                )}

                                                                {/* Delete button */}
                                                                {canDelete && (
                                                                    <button
                                                                        onClick={() => onDeleteQuote(quote.id)}
                                                                        className={`${styles.actionButton} ${styles.deleteButton}`}
                                                                        disabled={isEditing}
                                                                        title="Remover"
                                                                    >
                                                                        <FiTrash2 size={14} />
                                                                    </button>
                                                                )}

                                                                {/* Status badges */}
                                                                {isApproved && (
                                                                    <span className={styles.approvedBadgeSmall} title="Aprovada">
                                                                        <FiThumbsUp size={14} />
                                                                    </span>
                                                                )}
                                                                {isConverted && (
                                                                    <span className={styles.convertedBadgeSmall} title="Convertida em Pedido">
                                                                        <FiCheckCircle size={14} />
                                                                    </span>
                                                                )}
                                                                {isRejected && (
                                                                    <span className={styles.rejectedBadgeSmall} title="Rejeitada">
                                                                        <FiX size={14} />
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className={styles.noQuotesMessage}>
                                        <FiInfo size={16} />
                                        <span>Nenhuma cotação cadastrada.</span>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================
// 2.1 FORMULÁRIO DE COTAÇÃO POR FORNECEDOR
// ============================================

const SupplierQuoteForm = ({
    availableSuppliers,
    supplierItems,
    selectedSupplierId,
    selectedItemIds,
    onSupplierChange,
    onToggleItem,
    initialData,
    onSave,
    onCancel,
    isLoading,
    isEditing
}) => {
    const [formData, setFormData] = useState({
        supplierId: initialData?.supplierId || '',
        supplierName: initialData?.supplierName || '',
        supplierCode: initialData?.supplierCode || '',
        supplierAddress: initialData?.supplierAddress || '',
        deliveryTime: initialData?.deliveryTime || '',
        paymentTerms: initialData?.paymentTerms || '',
        observations: initialData?.observations || '',
        status: initialData?.status || SUPPLIER_QUOTE_STATUS.PENDING,
        items: initialData?.items || [],
        ipi: initialData?.ipi || 0,
        subtotal: initialData?.subtotal || 0,
        totalValue: initialData?.totalValue || 0
    });

    const [validationError, setValidationError] = useState('');

    const formatCurrency = (value) => {
        if (!value && value !== 0) return 'R$ 0,00';
        return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    };

    // Calculate subtotal (sum of items without IPI)
    const subtotal = useMemo(() => {
        const items = formData.items.filter(item => selectedItemIds.includes(item.itemId));
        return items.reduce((sum, item) => {
            const price = parseFloat(item.unitPrice) || 0;
            const qty = parseFloat(item.quantity) || 0;
            return sum + (price * qty);
        }, 0);
    }, [formData.items, selectedItemIds]);

    // Calculate total with IPI
    const totalValue = useMemo(() => {
        const ipiRate = parseFloat(formData.ipi) || 0;
        return subtotal * (1 + ipiRate / 100);
    }, [subtotal, formData.ipi]);

    // Update formData when total changes
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            subtotal: subtotal,
            totalValue: totalValue
        }));
    }, [subtotal, totalValue]);

    const supplierOptions = useMemo(() => {
        if (!availableSuppliers) return [];
        return availableSuppliers.map(s => ({
            id: s.id,
            value: s.id,
            label: `${s.codigo || ''} - ${s.razaoSocial}`,
            razaoSocial: s.razaoSocial,
            codigo: s.codigo || '',
            addresses: s.addresses || []
        }));
    }, [availableSuppliers]);

    const handleSupplierChange = (supplierId) => {
        const supplier = availableSuppliers?.find(s => s.id === supplierId);
        onSupplierChange(supplierId);
        setFormData(prev => ({
            ...prev,
            supplierId: supplierId,
            supplierName: supplier?.razaoSocial || '',
            supplierCode: supplier?.codigo || '',
            supplierAddress: supplier?.addresses?.[0]
                ? `${supplier.addresses[0].endereco || ''}, ${supplier.addresses[0].numero || ''} - ${supplier.addresses[0].cidade || ''}/${supplier.addresses[0].uf || ''}`
                : ''
        }));
    };

    const handleItemPriceChange = (itemId, field, value) => {
        setFormData(prev => {
            const updatedItems = prev.items.map(item => {
                if (item.itemId === itemId) {
                    const updated = { ...item, [field]: parseFloat(value) || 0 };
                    if (field === 'unitPrice') {
                        const qty = parseFloat(item.quantity) || 0;
                        updated.totalPrice = updated.unitPrice * qty;
                    }
                    return updated;
                }
                return item;
            });
            return { ...prev, items: updatedItems };
        });
    };

    useEffect(() => {
        if (selectedSupplierId && selectedItemIds.length > 0) {
            const items = supplierItems
                .filter(item => selectedItemIds.includes(item.itemId))
                .map(item => {
                    const existingItem = formData.items.find(i => i.itemId === item.itemId);
                    if (existingItem) {
                        return existingItem;
                    }
                    const unitPrice = parseFloat(item.unitPrice) || 0;
                    const quantity = parseFloat(item.quantity) || 0;
                    return {
                        itemId: item.itemId,
                        itemCode: item.itemCode,
                        itemName: item.itemName,
                        quantity: quantity,
                        unitPrice: unitPrice,
                        totalPrice: unitPrice * quantity
                    };
                });
            setFormData(prev => ({ ...prev, items }));
        }
    }, [selectedSupplierId, selectedItemIds, supplierItems]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.supplierId) {
            setValidationError('Selecione um fornecedor.');
            return;
        }

        if (selectedItemIds.length === 0) {
            setValidationError('Selecione pelo menos um item para cotar.');
            return;
        }

        const itemsWithPrices = formData.items.filter(item =>
            selectedItemIds.includes(item.itemId) &&
            (parseFloat(item.unitPrice) || 0) > 0
        );

        if (itemsWithPrices.length !== selectedItemIds.length) {
            setValidationError('Informe o preço unitário para todos os itens selecionados.');
            return;
        }

        if (!formData.deliveryTime) {
            setValidationError('Informe o prazo de entrega.');
            return;
        }

        const items = formData.items.filter(item => selectedItemIds.includes(item.itemId));

        const finalData = {
            ...formData,
            items: items,
            subtotal: subtotal,
            totalValue: totalValue,
            itemIds: selectedItemIds,
            ipi: parseFloat(formData.ipi) || 0
        };

        onSave(finalData);
    };

    return (
        <form onSubmit={handleSubmit} className={styles.quoteForm}>
            {validationError && (
                <div className={styles.errorMessage}>
                    <FiAlertCircle size={16} /> {validationError}
                </div>
            )}

            <div className={styles.formGrid}>
                <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                    <label>Fornecedor <span className={styles.required}>*</span></label>
                    <ComboBox
                        options={supplierOptions}
                        value={formData.supplierId}
                        onChange={handleSupplierChange}
                        placeholder="Selecione um fornecedor..."
                        disabled={isLoading || isEditing}
                        getOptionLabel={(opt) => opt.label}
                        getOptionValue={(opt) => opt.id}
                        getOptionKey={(opt) => opt.id}
                    />
                    {formData.supplierId && supplierItems.length === 0 && (
                        <small className={styles.fieldHint} style={{ color: '#f44336' }}>
                            Este fornecedor não possui itens disponíveis para cotação.
                        </small>
                    )}
                </div>

                {selectedSupplierId && supplierItems.length > 0 && (
                    <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                        <label>Itens para Cotação <span className={styles.required}>*</span></label>
                        <div className={styles.itemChecklist}>
                            {supplierItems.map(item => {
                                const isSelected = selectedItemIds.includes(item.itemId);
                                const existingItem = formData.items.find(i => i.itemId === item.itemId);
                                return (
                                    <div key={item.itemId} className={styles.checklistItem}>
                                        <label className={styles.checklistLabel}>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => onToggleItem(item.itemId)}
                                                disabled={isLoading}
                                            />
                                            <span className={styles.itemCode}>{item.itemCode}</span>
                                            <span className={styles.itemName}>{item.itemName}</span>
                                            <span className={styles.itemQty}>Qtde: {item.quantity}</span>
                                        </label>
                                        {isSelected && (
                                            <div className={styles.itemPriceInput}>
                                                <input
                                                    type="number"
                                                    step="0.00001"
                                                    value={existingItem?.unitPrice || ''}
                                                    onChange={(e) => handleItemPriceChange(item.itemId, 'unitPrice', e.target.value)}
                                                    className={styles.input}
                                                    disabled={isLoading}
                                                    placeholder="Preço unitário"
                                                    style={{ width: '150px' }}
                                                />
                                                <span className={styles.itemTotal}>
                                                    Total: {formatCurrency((parseFloat(existingItem?.unitPrice) || 0) * (parseFloat(item.quantity) || 0))}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <small className={styles.fieldHint}>
                            Selecione os itens que serão cotados com este fornecedor.
                        </small>
                    </div>
                )}

                {selectedItemIds.length > 0 && (
                    <>
                        <div className={styles.inputGroup}>
                            <label>Prazo de Entrega (dias) <span className={styles.required}>*</span></label>
                            <input
                                type="number"
                                value={formData.deliveryTime}
                                onChange={(e) => setFormData(prev => ({ ...prev, deliveryTime: e.target.value }))}
                                className={styles.input}
                                disabled={isLoading}
                                placeholder="30"
                                required
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>Condições de Pagamento</label>
                            <input
                                type="text"
                                value={formData.paymentTerms}
                                onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: e.target.value }))}
                                className={styles.input}
                                disabled={isLoading}
                                placeholder="30/60/90 dias"
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>IPI (%)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.ipi}
                                onChange={(e) => setFormData(prev => ({ ...prev, ipi: parseFloat(e.target.value) || 0 }))}
                                className={styles.input}
                                disabled={isLoading}
                                placeholder="0.00"
                                min="0"
                            />
                            <small className={styles.fieldHint}>Percentual de IPI sobre o subtotal</small>
                        </div>
                        <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                            <label>Observações da Cotação</label>
                            <textarea
                                value={formData.observations}
                                onChange={(e) => setFormData(prev => ({ ...prev, observations: e.target.value }))}
                                className={styles.textarea}
                                rows={2}
                                disabled={isLoading}
                                placeholder="Observações sobre esta cotação..."
                            />
                        </div>
                        <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                            <div className={styles.quoteSummary}>
                                <div className={styles.summaryRow}>
                                    <span className={styles.summaryLabel}>Subtotal:</span>
                                    <span className={styles.summaryValue}>
                                        {formatCurrency(subtotal)}
                                    </span>
                                </div>
                                {parseFloat(formData.ipi) > 0 && (
                                    <div className={styles.summaryRow}>
                                        <span className={styles.summaryLabel}>IPI ({formData.ipi}%):</span>
                                        <span className={styles.summaryValue}>
                                            {formatCurrency(subtotal * (parseFloat(formData.ipi) || 0) / 100)}
                                        </span>
                                    </div>
                                )}
                                <div className={styles.summaryRowTotal}>
                                    <span className={styles.summaryLabel}>Valor Total da Cotação:</span>
                                    <span className={styles.summaryValueTotal}>
                                        {formatCurrency(totalValue)}
                                    </span>
                                </div>
                                <span className={styles.summaryDetail}>
                                    ({selectedItemIds.length} item(ns))
                                </span>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className={styles.quoteFormActions}>
                <button
                    type="button"
                    onClick={onCancel}
                    className={styles.secondaryButton}
                    disabled={isLoading}
                >
                    <FiX size={18} /> Cancelar
                </button>
                <button
                    type="submit"
                    className={styles.primaryButton}
                    disabled={isLoading || selectedItemIds.length === 0}
                >
                    {isLoading ? (
                        <><FiRefreshCw size={18} className={styles.buttonSpinner} /> Salvando...</>
                    ) : (
                        <><FiSave size={18} /> {isEditing ? 'Atualizar' : 'Adicionar'} Cotação</>
                    )}
                </button>
            </div>
        </form>
    );
};

// ============================================
// 3. MAIN COMPONENT
// ============================================
const PurchaseRequestPage = () => {
    // ============================================
    // 3.1 PERMISSIONS
    // ============================================
    const can = usePagePermissions('requisicao_de_compra');

    // ============================================
    // 3.2 ESTADOS PRINCIPAIS
    // ============================================
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [editingRequest, setEditingRequest] = useState(null);
    const [viewingRequest, setViewingRequest] = useState(null);
    const [viewingOrder, setViewingOrder] = useState(null);
    const [formStatus, setFormStatus] = useState({ message: '', type: '' });
    const [formLoading, setFormLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [toasts, setToasts] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedSections, setExpandedSections] = useState({});
    const [printModalData, setPrintModalData] = useState(null);
    const [rejectQuoteModal, setRejectQuoteModal] = useState(null);

    // Estados do Formulário (Step 1 - Criação)
    const [selectedMaterialId, setSelectedMaterialId] = useState('');
    const [requestItems, setRequestItems] = useState([]);

    // Estados do Formulário (Step 2 - Informações)
    const [requestCode, setRequestCode] = useState('');
    const [requestDate, setRequestDate] = useState('');
    const [requestType, setRequestType] = useState(REQUEST_TYPES[0].value);
    const [priority, setPriority] = useState(PRIORITY_OPTIONS[1].value);
    const [requestedBy, setRequestedBy] = useState('');

    // Estados para Revisão e Cotações
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [requiredDate, setRequiredDate] = useState('');
    const [department, setDepartment] = useState('');

    // Supplier Quotes state
    const [supplierQuotes, setSupplierQuotes] = useState([]);

    // ============================================
    // 3.3 HOOKS DE DADOS
    // ============================================
    const { currentUser } = useAuth();
    const { addNotification } = useNotifications();

    const {
        data: requests,
        loading: requestsLoading,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData: refreshRequests
    } = useFirestore(PURCHASE_REQUESTS_COLLECTION, 'createdAt', true);

    const { data: suppliers } = useFirestore(SUPPLIERS_COLLECTION, 'razaoSocial');
    const { data: rawMaterials } = useFirestore(RAW_MATERIALS_COLLECTION, 'code');

    // ============================================
    // 3.4 SHOW SKELETON
    // ============================================
    const showSkeleton = useMemo(() => {
        if (requestsLoading && (!requests || requests.length === 0)) return true;
        return false;
    }, [requestsLoading, requests]);

    // ============================================
    // 3.5 TOAST FUNCTIONS
    // ============================================
    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    // ============================================
    // 3.6 FUNÇÕES AUXILIARES
    // ============================================

    const generateRequestCode = useCallback(async () => {
        const currentYear = new Date().getFullYear();
        const yearSuffix = currentYear.toString().slice(-2);

        if (!requests || requests.length === 0) {
            return `RC001${yearSuffix}`;
        }

        const thisYearRequests = requests.filter(req => {
            if (!req.code) return false;
            return req.code.includes(yearSuffix);
        });

        if (thisYearRequests.length === 0) {
            return `RC001${yearSuffix}`;
        }

        let maxNumber = 0;
        thisYearRequests.forEach(req => {
            const match = req.code.match(/RC(\d{3})\d{2}/);
            if (match) {
                const num = parseInt(match[1], 10);
                if (!isNaN(num) && num > maxNumber) {
                    maxNumber = num;
                }
            }
        });

        const nextNumber = (maxNumber + 1).toString().padStart(3, '0');
        return `RC${nextNumber}${yearSuffix}`;
    }, [requests]);

    const responsible = useMemo(() => {
        if (currentUser) {
            return currentUser.nome || currentUser.email?.split('@')[0] || 'Usuário';
        }
        return 'Usuário';
    }, [currentUser]);

    useEffect(() => {
        if (isModalOpen && !editingRequest) {
            const today = new Date();
            setRequestDate(today.toLocaleDateString('pt-BR'));
            setRequestedBy(responsible);
        }
    }, [isModalOpen, editingRequest, responsible]);

    useEffect(() => {
        if (isModalOpen && !editingRequest) {
            const generateCode = async () => {
                const code = await generateRequestCode();
                setRequestCode(code);
            };
            generateCode();
        }
    }, [isModalOpen, editingRequest, generateRequestCode]);

    const materialOptions = useMemo(() => {
        if (!rawMaterials) return [];
        return rawMaterials.map(material => ({
            id: material.id,
            value: material.id,
            label: `[${material.code}] ${material.commercialName}${material.inciName ? ` - ${material.inciName}` : ''}`,
            material: material,
            suppliers: material.suppliers || []
        }));
    }, [rawMaterials]);

    const calculateItemTotal = (quantity, unitPrice) => {
        const qty = parseFloat(quantity) || 0;
        const price = parseFloat(unitPrice) || 0;
        return qty * price;
    };

    const itemsSubtotal = useMemo(() => {
        return requestItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    }, [requestItems]);

    // ============================================
    // 3.7 HANDLERS DO FORMULÁRIO
    // ============================================

    const resetForm = () => {
        setEditingRequest(null);
        setIsModalOpen(false);
        setIsReviewModalOpen(false);
        setRequestCode('');
        setTitle('');
        setDescription('');
        setRequestType(REQUEST_TYPES[0].value);
        setPriority(PRIORITY_OPTIONS[1].value);
        setRequestDate('');
        setRequiredDate('');
        setDepartment('');
        setRequestedBy(responsible);
        setRequestItems([]);
        setSelectedMaterialId('');
        setSupplierQuotes([]);
        setFormStatus({ message: '', type: '' });
        setExpandedSections({});
        setRejectQuoteModal(null);
        setPrintModalData(null);
    };

    // ============================================
    // 3.7.1 NOTIFICATION FUNCTIONS
    // ============================================

    const notifyDiretoriaAndOperacional = useCallback(async (requestData, requestCode, requestId) => {
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const targetUsers = [];

            usersSnapshot.forEach(doc => {
                const userData = doc.data();
                const roles = Array.isArray(userData.role) ? userData.role : [userData.role];

                if (roles.some(r => r === 'Diretoria' || r === 'Diretoria (Admin)' || r === 'Operacional')) {
                    targetUsers.push({
                        uid: doc.id,
                        ...userData
                    });
                }
            });

            const priorityLabel = PRIORITY_OPTIONS.find(p => p.value === requestData.priority)?.label || requestData.priority;

            const baseNotification = {
                type: 'new_purchase_request',
                module: 'purchase',
                title: `🛒 Nova Solicitação de Compra`,
                fullMessage: `Detalhes da solicitação:\n• Código: ${requestCode}\n• Solicitante: ${requestData.requestedBy || 'Usuário'}\n• Itens: ${requestData.items?.length || 0}\n• Prioridade: ${priorityLabel}\n• Descrição: ${requestData.description || 'N/A'}`,
                timestamp: new Date().toISOString(),
                read: false,
                priority: requestData.priority || 'medium',
                route: `/purchase-requests`,
                data: {
                    requestId: requestId,
                    requestCode: requestCode,
                    requestedBy: requestData.requestedBy || 'Usuário',
                    totalItems: requestData.items?.length || 0,
                    totalValue: requestData.totalValue || 0
                }
            };

            for (const user of targetUsers) {
                if (user.uid === currentUser?.uid) continue;

                addNotification({
                    ...baseNotification,
                    id: `purchase_${requestId}_${user.uid}_${Date.now()}`,
                    message: `${user.nome || 'Usuário'}, ${requestData.requestedBy || 'Alguém'} criou uma nova solicitação de compra ${requestCode} - ${requestData.items?.length || 0} item(ns) - Prioridade: ${priorityLabel}`,
                    userId: user.uid
                });

                try {
                    await addDoc(collection(db, 'notifications'), {
                        userId: user.uid,
                        ...baseNotification,
                        id: `purchase_${requestId}_${user.uid}_${Date.now()}`,
                        message: `${user.nome || 'Usuário'}, ${requestData.requestedBy || 'Alguém'} criou uma nova solicitação de compra ${requestCode} - ${requestData.items?.length || 0} item(ns) - Prioridade: ${priorityLabel}`,
                        createdAt: new Date()
                    });
                } catch (err) {
                    console.warn(`Erro ao salvar notificação para ${user.email}:`, err);
                }
            }
        } catch (error) {
            console.error('❌ Erro ao enviar notificações:', error);
        }
    }, [currentUser, addNotification]);

    const notifyStatusChange = useCallback(async (request, statusText, emoji) => {
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));

            const creatorUser = usersSnapshot.docs.find(doc => {
                const data = doc.data();
                return data.nome === request.requestedBy || data.email === request.requestedBy;
            });

            const baseNotification = {
                type: 'purchase_status_change',
                module: 'purchase',
                title: `${emoji} Solicitação ${statusText}`,
                fullMessage: `Detalhes:\n• Código: ${request.code}\n• Status: ${statusText}\n• Atualizado por: ${responsible}\n• Data: ${new Date().toLocaleDateString('pt-BR')}`,
                timestamp: new Date().toISOString(),
                read: false,
                priority: statusText === 'aprovada' ? 'high' : 'medium',
                route: `/purchase-requests`,
                data: {
                    requestId: request.id,
                    requestCode: request.code,
                    newStatus: statusText,
                    updatedBy: responsible
                }
            };

            if (creatorUser && creatorUser.id !== currentUser?.uid) {
                addNotification({
                    ...baseNotification,
                    id: `purchase_status_${request.id}_${creatorUser.id}_${Date.now()}`,
                    message: `Solicitação ${request.code} foi ${statusText} por ${responsible}`,
                    userId: creatorUser.id
                });

                try {
                    await addDoc(collection(db, 'notifications'), {
                        userId: creatorUser.id,
                        ...baseNotification,
                        id: `purchase_status_${request.id}_${creatorUser.id}_${Date.now()}`,
                        message: `Solicitação ${request.code} foi ${statusText} por ${responsible}`,
                        createdAt: new Date()
                    });
                } catch (err) {
                    console.warn('Erro ao salvar notificação para criador:', err);
                }
            }

            const targetUsers = usersSnapshot.docs.filter(doc => {
                const data = doc.data();
                const roles = Array.isArray(data.role) ? data.role : [data.role];
                return roles.some(r => r === 'Diretoria' || r === 'Diretoria (Admin)' || r === 'Operacional');
            });

            for (const user of targetUsers) {
                if (user.id !== currentUser?.uid && user.id !== creatorUser?.id) {
                    addNotification({
                        ...baseNotification,
                        id: `purchase_status_${request.id}_${user.id}_${Date.now()}`,
                        message: `Solicitação ${request.code} foi ${statusText} por ${responsible}`,
                        userId: user.id
                    });

                    try {
                        await addDoc(collection(db, 'notifications'), {
                            userId: user.id,
                            ...baseNotification,
                            id: `purchase_status_${request.id}_${user.id}_${Date.now()}`,
                            message: `Solicitação ${request.code} foi ${statusText} por ${responsible}`,
                            createdAt: new Date()
                        });
                    } catch (err) {
                        console.warn('Erro ao salvar notificação para diretoria/operacional:', err);
                    }
                }
            }
        } catch (error) {
            console.error('Erro ao notificar mudança de status:', error);
        }
    }, [currentUser, responsible, addNotification]);

    // ============================================
    // 3.8 HANDLERS DO FORMULÁRIO PRINCIPAL
    // ============================================

    const handleCreateRequest = async () => {
        if (requestItems.length === 0) {
            setFormStatus({ message: 'Adicione pelo menos um item à solicitação.', type: 'error' });
            return;
        }

        setFormLoading(true);
        setFormStatus({ message: '', type: '' });

        const baseData = {
            title: `${requestItems.length} item(ns) - ${requestType}`,
            description: description,
            requestType,
            priority,
            requestDate,
            requiredDate: editingRequest?.requiredDate || '',
            department: editingRequest?.department || '',
            requestedBy,
            items: requestItems.map(item => ({
                ...item,
                quantity: parseFloat(item.quantity) || 0,
                unitPrice: parseFloat(item.unitPrice) || 0,
                total: parseFloat(item.total) || 0,
                suppliers: item.suppliers || []
            })),
            supplierQuotes: supplierQuotes.map(q => ({
                ...q,
                items: q.items || [],
                subtotal: parseFloat(q.subtotal) || 0,
                totalValue: parseFloat(q.totalValue) || 0,
                ipi: parseFloat(q.ipi) || 0,
                rejectionReason: q.rejectionReason || null,
                rejectedAt: q.rejectedAt || null,
                rejectedBy: q.rejectedBy || null,
                approvedAt: q.approvedAt || null,
                approvedBy: q.approvedBy || null,
                convertedAt: q.convertedAt || null,
                convertedToOrderId: q.convertedToOrderId || null,
                convertedToOrderCode: q.convertedToOrderCode || null
            })),
            totalValue: itemsSubtotal,
            updatedAt: new Date(),
            responsible: responsible
        };

        try {
            let result;
            if (editingRequest) {
                result = await updateDocument(editingRequest.id, baseData);
                if (result.success) {
                    addToast(`Solicitação ${requestCode} atualizada com sucesso!`, 'Success');
                    resetForm();
                    if (refreshRequests) await refreshRequests();
                } else {
                    setFormStatus({ message: `Erro: ${result.error}`, type: 'error' });
                }
            } else {
                const newRequestData = {
                    ...baseData,
                    code: requestCode,
                    createdAt: new Date(),
                    status: REQUEST_STATUS.PENDING,
                    trackingHistory: [{
                        date: new Date().toLocaleDateString('pt-BR'),
                        status: 'Criada',
                        description: 'Solicitação criada e enviada para avaliação.',
                        updatedBy: responsible
                    }]
                };

                result = await addDocument(newRequestData);

                if (result.success) {
                    addToast(`Solicitação ${requestCode} criada com sucesso!`, 'Success');
                    await notifyDiretoriaAndOperacional(newRequestData, requestCode, result.id);
                    resetForm();
                    if (refreshRequests) await refreshRequests();
                } else {
                    setFormStatus({ message: `Erro: ${result.error}`, type: 'error' });
                }
            }
        } catch (error) {
            setFormStatus({ message: `Erro ao salvar: ${error.message}`, type: 'error' });
        } finally {
            setFormLoading(false);
        }
    };

    const handleAddMaterial = () => {
        if (!selectedMaterialId) return;

        const selectedOption = materialOptions.find(opt => opt.value === selectedMaterialId);
        if (!selectedOption) return;

        const material = selectedOption.material;
        const materialSuppliers = selectedOption.suppliers || [];

        const fullSuppliers = materialSuppliers.map(s => {
            const supplier = suppliers?.find(sup => sup.id === s.supplierId);
            return supplier ? {
                ...s,
                razaoSocial: supplier.razaoSocial,
                codigo: supplier.codigo
            } : s;
        });

        const generateUniqueId = () => {
            return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        };

        const newItem = {
            id: generateUniqueId(),
            materialId: material.id,
            code: material.code,
            name: material.commercialName,
            inciName: material.inciName || '',
            quantity: '1',
            unitPrice: '',
            total: '0',
            observations: '',
            suppliers: fullSuppliers
        };

        setRequestItems(prev => [...prev, newItem]);
        setSelectedMaterialId('');
        addToast('Item adicionado à solicitação.', 'Success');
    };

    const handleRemoveItem = (itemId) => {
        setRequestItems(prev => prev.filter(item => item.id !== itemId));
        setSupplierQuotes(prev => prev.map(q => ({
            ...q,
            items: q.items.filter(item => item.itemId !== itemId)
        })).filter(q => q.items.length > 0));
    };

    const handleUpdateItem = (itemId, field, value) => {
        setRequestItems(prev => prev.map(item => {
            if (item.id === itemId) {
                const updated = { ...item, [field]: value };
                if (field === 'quantity' || field === 'unitPrice') {
                    const total = calculateItemTotal(
                        field === 'quantity' ? value : item.quantity,
                        field === 'unitPrice' ? value : item.unitPrice
                    );
                    updated.total = total.toFixed(2);
                }
                return updated;
            }
            return item;
        }));
    };

    // ============================================
    // 3.9 QUOTE HANDLERS - INDIVIDUAL APPROVAL & CONVERSION
    // ============================================

    const handleAddSupplierQuote = (quoteData) => {
        const generateUniqueId = () => {
            return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        };

        const newQuote = {
            id: generateUniqueId(),
            ...quoteData,
            status: SUPPLIER_QUOTE_STATUS.PENDING,
            createdAt: new Date(),
            items: quoteData.items || [],
            ipi: parseFloat(quoteData.ipi) || 0,
            subtotal: quoteData.subtotal || 0,
            totalValue: quoteData.totalValue || 0
        };
        setSupplierQuotes(prev => [...prev, newQuote]);
        addToast('Cotação adicionada com sucesso!', 'Success');
    };

    const handleEditSupplierQuote = (quoteId, quoteData) => {
        setSupplierQuotes(prev => prev.map(q =>
            q.id === quoteId ? {
                ...quoteData,
                id: q.id,
                ipi: parseFloat(quoteData.ipi) || 0,
                subtotal: quoteData.subtotal || 0,
                totalValue: quoteData.totalValue || 0
            } : q
        ));
        addToast('Cotação atualizada.', 'Success');
    };

    const handleDeleteSupplierQuote = (quoteId) => {
        setSupplierQuotes(prev => prev.filter(q => q.id !== quoteId));
        addToast('Cotação removida.', 'Info');
    };

    const handleSelectSupplierQuote = (quoteId) => {
        setSupplierQuotes(prev => prev.map(q => ({
            ...q,
            status: q.id === quoteId ? SUPPLIER_QUOTE_STATUS.SELECTED :
                q.status === SUPPLIER_QUOTE_STATUS.SELECTED ? SUPPLIER_QUOTE_STATUS.QUOTED : q.status
        })));
        addToast('Cotação selecionada.', 'Success');
    };

    // ✅ Approve individual quote
    const handleApproveSupplierQuote = (quoteId) => {
        setSupplierQuotes(prev => prev.map(q => ({
            ...q,
            status: q.id === quoteId ? SUPPLIER_QUOTE_STATUS.APPROVED : q.status,
            approvedAt: q.id === quoteId ? new Date() : q.approvedAt,
            approvedBy: q.id === quoteId ? responsible : q.approvedBy
        })));
        addToast('Cotação aprovada! Agora pode ser convertida em Pedido de Compra.', 'Success');
    };

    const handleRejectSupplierQuote = (quoteId, rejectionReason = '') => {
        setSupplierQuotes(prev => prev.map(q => ({
            ...q,
            status: q.id === quoteId ? SUPPLIER_QUOTE_STATUS.REJECTED : q.status,
            rejectionReason: q.id === quoteId ? rejectionReason : q.rejectionReason,
            rejectedAt: q.id === quoteId ? new Date() : q.rejectedAt,
            rejectedBy: q.id === quoteId ? responsible : q.rejectedBy
        })));
        addToast('Cotação rejeitada.', 'Info');
    };

    const handleOpenRejectModal = (quote) => {
        setRejectQuoteModal({
            quoteId: quote.id,
            quote: quote,
            reason: ''
        });
    };

    const handleConfirmReject = () => {
        if (rejectQuoteModal) {
            handleRejectSupplierQuote(rejectQuoteModal.quoteId, rejectQuoteModal.reason);
            setRejectQuoteModal(null);
        }
    };

    // ✅ Convert individual approved quote to Purchase Order
    const handleConvertApprovedQuote = async (quoteId) => {
        console.log('🔍 handleConvertApprovedQuote chamado com quoteId:', quoteId);
        console.log('📋 supplierQuotes atual:', supplierQuotes);
        console.log('📋 editingRequest:', editingRequest);

        // Buscar a solicitação atual - usar o editingRequest que está em memória
        const requestData = editingRequest;

        if (!requestData) {
            addToast('Solicitação não encontrada. Abra a revisão novamente.', 'Error');
            return;
        }

        // Buscar a cotação no estado local supplierQuotes (mais confiável)
        const quote = supplierQuotes.find(q => q.id === quoteId);

        if (!quote) {
            console.error('❌ Cotação não encontrada. quoteId:', quoteId);
            console.log('📋 Cotações disponíveis:', supplierQuotes.map(q => ({ id: q.id, supplierName: q.supplierName })));
            addToast('Cotação não encontrada no estado atual.', 'Error');
            return;
        }

        console.log('✅ Cotação encontrada:', quote);

        // Verificar se a cotação está aprovada
        if (quote.status !== SUPPLIER_QUOTE_STATUS.APPROVED) {
            addToast('Apenas cotações aprovadas podem ser convertidas em Pedidos de Compra.', 'Error');
            return;
        }

        // Verificar se já foi convertida
        if (quote.status === SUPPLIER_QUOTE_STATUS.CONVERTED) {
            addToast('Esta cotação já foi convertida em um Pedido de Compra.', 'Error');
            return;
        }

        setFormLoading(true);

        try {
            // Buscar pedidos existentes para gerar código sequencial
            const ordersSnapshot = await getDocs(collection(db, PURCHASE_ORDERS_COLLECTION));
            const existingOrders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            let maxNumber = 0;
            existingOrders.forEach(order => {
                if (order.code) {
                    const match = order.code.match(/^PV(\d{3})/);
                    if (match) {
                        const num = parseInt(match[1], 10);
                        if (num > maxNumber) maxNumber = num;
                    }
                }
            });

            const currentYear = new Date().getFullYear();
            const yearSuffix = currentYear.toString().slice(-2);
            const nextNumber = maxNumber + 1;
            const nextNumberStr = nextNumber.toString().padStart(3, '0');
            const orderCode = `PV${nextNumberStr}${yearSuffix}`;

            const supplier = suppliers?.find(s => s.id === quote.supplierId);

            // Criar os itens do pedido a partir da cotação
            const orderItems = quote.items.map(item => {
                const originalItem = requestData.items.find(i => i.id === item.itemId);
                return {
                    id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    materialId: originalItem?.materialId || '',
                    code: item.itemCode || '',
                    name: item.itemName || '',
                    inciName: originalItem?.inciName || '',
                    quantity: parseFloat(item.quantity) || 0,
                    unitPrice: parseFloat(item.unitPrice) || 0,
                    total: (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0),
                    supplierSpecifics: {
                        supplierId: quote.supplierId,
                        supplierName: quote.supplierName || supplier?.razaoSocial || '',
                        valor: parseFloat(item.unitPrice) || 0
                    },
                    ncmId: originalItem?.ncmId || null,
                    ncmCode: originalItem?.ncmCode || null,
                    quoteId: quote.id
                };
            });

            const totalValue = orderItems.reduce((sum, item) => sum + (item.total || 0), 0);

            // Criar o pedido de compra
            const orderData = {
                code: orderCode,
                supplierId: quote.supplierId,
                supplierName: quote.supplierName || supplier?.razaoSocial || '',
                supplierCode: quote.supplierCode || supplier?.codigo || '',
                supplierAddress: quote.supplierAddress || '',
                orderDate: new Date().toLocaleDateString('pt-BR'),
                items: orderItems,
                calculatedValues: {
                    subtotal: totalValue,
                    discountAmount: 0,
                    subtotalWithDiscount: totalValue,
                    ipi: quote.ipi || 0,
                    icms: 0,
                    icmsSt: 0,
                    pis: 0,
                    cofins: 0,
                    ii: 0,
                    freight: 0,
                    total: totalValue * (1 + (quote.ipi || 0) / 100),
                    valorNetUsd: (totalValue * (1 + (quote.ipi || 0) / 100)) / 1
                },
                originalRequestId: requestData.id,
                originalRequestCode: requestData.code,
                originalRequestTitle: requestData.title || '',
                originalQuoteId: quote.id,
                status: 'pending',
                createdAt: new Date(),
                updatedAt: new Date(),
                responsible: responsible,
                selectedQuote: quote || null,
                deliveryTime: quote?.deliveryTime || '',
                paymentTerms: quote?.paymentTerms || '',
                commercialNotes: `Cotação da Solicitação ${requestData.code} - Fornecedor ${quote.supplierName}`,
                supplierAccount: '',
                discountPercent: 0,
                dollarType: 'Dolar Negociado',
                dollarValue: 1.0000,
                dollarFaturamento: 0,
                dollarMedio: 0,
                dollarDia: 0,
                dollarNegociado: 1.0000,
                ipiValue: (totalValue * (quote.ipi || 0) / 100),
                icmsValue: 0,
                icmsStValue: 0,
                pisValue: 0,
                cofinsValue: 0,
                iiValue: 0,
                freightValue: 0,
                deliveryType: 'transportadora',
                freightBy: 'emitente',
                selectedCarrierId: '',
                selectedCarrierName: '',
                selectedBillingConditionId: '',
                selectedBillingConditionName: '',
                paymentDeadline: '',
                sendToReceiving: false,
                sendEmail: false,
                convertedFromQuoteId: quote.id,
                conversionDate: new Date()
            };

            // Salvar o pedido no Firestore
            const orderRef = await addDoc(collection(db, PURCHASE_ORDERS_COLLECTION), orderData);

            if (orderRef.id) {
                // Atualizar o status da cotação para CONVERTED
                const updatedQuotes = supplierQuotes.map(q => ({
                    ...q,
                    status: q.id === quote.id ? SUPPLIER_QUOTE_STATUS.CONVERTED : q.status,
                    convertedAt: q.id === quote.id ? new Date() : q.convertedAt,
                    convertedToOrderId: q.id === quote.id ? orderRef.id : q.convertedToOrderId,
                    convertedToOrderCode: q.id === quote.id ? orderCode : q.convertedToOrderCode
                }));

                // Atualizar o estado local
                setSupplierQuotes(updatedQuotes);

                // Verificar quantas cotações estão em cada status
                const approvedQuotes = updatedQuotes.filter(q => q.status === SUPPLIER_QUOTE_STATUS.APPROVED);
                const convertedQuotes = updatedQuotes.filter(q => q.status === SUPPLIER_QUOTE_STATUS.CONVERTED);
                const rejectedQuotes = updatedQuotes.filter(q => q.status === SUPPLIER_QUOTE_STATUS.REJECTED);
                const pendingQuotes = updatedQuotes.filter(q => q.status === SUPPLIER_QUOTE_STATUS.PENDING || q.status === SUPPLIER_QUOTE_STATUS.QUOTED || q.status === SUPPLIER_QUOTE_STATUS.SELECTED);
                const totalQuotes = updatedQuotes.length;

                // Determinar o novo status da solicitação - FIXED LOGIC
                let newRequestStatus;
                if (totalQuotes === 0) {
                    newRequestStatus = REQUEST_STATUS.UNDER_REVIEW;
                } else if (convertedQuotes.length === totalQuotes) {
                    // All quotes were converted
                    newRequestStatus = REQUEST_STATUS.CONVERTED;
                } else if (convertedQuotes.length > 0 && (approvedQuotes.length > 0 || pendingQuotes.length > 0 || rejectedQuotes.length > 0)) {
                    // Some converted, but others exist (approved, pending, or rejected) - should remain partially converted
                    newRequestStatus = REQUEST_STATUS.PARTIALLY_CONVERTED;
                } else if (approvedQuotes.length > 0 && convertedQuotes.length === 0 && pendingQuotes.length === 0 && rejectedQuotes.length === 0) {
                    // All approved, none pending, none converted, none rejected
                    newRequestStatus = REQUEST_STATUS.APPROVED;
                } else if (approvedQuotes.length > 0 && pendingQuotes.length > 0 && convertedQuotes.length === 0) {
                    // Some approved, some pending - still in quoting phase
                    newRequestStatus = REQUEST_STATUS.QUOTING;
                } else if (pendingQuotes.length > 0 && approvedQuotes.length === 0 && convertedQuotes.length === 0 && rejectedQuotes.length === 0) {
                    // All pending or quoted
                    newRequestStatus = REQUEST_STATUS.QUOTING;
                } else if (rejectedQuotes.length === totalQuotes) {
                    // All rejected
                    newRequestStatus = REQUEST_STATUS.REJECTED;
                } else if (rejectedQuotes.length > 0 && (pendingQuotes.length > 0 || approvedQuotes.length > 0)) {
                    // Some rejected, some still active
                    newRequestStatus = REQUEST_STATUS.QUOTING;
                } else {
                    newRequestStatus = requestData.status || REQUEST_STATUS.UNDER_REVIEW;
                }

                // Atualizar o histórico
                const existingHistory = requestData.trackingHistory || [];
                const newHistoryEntry = {
                    date: new Date().toLocaleDateString('pt-BR'),
                    status: 'Cotação Convertida',
                    description: `Cotação do fornecedor ${quote.supplierName} convertida para Pedido de Compra ${orderCode}`,
                    updatedBy: responsible,
                    quoteId: quote.id,
                    orderId: orderRef.id,
                    orderCode: orderCode
                };

                // Atualizar a solicitação no Firestore
                await updateDoc(doc(db, PURCHASE_REQUESTS_COLLECTION, requestData.id), {
                    supplierQuotes: updatedQuotes,
                    status: newRequestStatus,
                    trackingHistory: [...existingHistory, newHistoryEntry],
                    updatedAt: new Date(),
                    ...(newRequestStatus === REQUEST_STATUS.CONVERTED && {
                        convertedAt: new Date(),
                        convertedBy: responsible
                    })
                });

                addToast(`Cotação convertida para Pedido de Compra ${orderCode}!`, 'Success');

                // Atualizar o editingRequest
                const updatedEditingRequest = {
                    ...editingRequest,
                    supplierQuotes: updatedQuotes,
                    status: newRequestStatus
                };
                setEditingRequest(updatedEditingRequest);

                // Esperar um pouco e atualizar a lista
                await new Promise(resolve => setTimeout(resolve, 300));
                if (refreshRequests) await refreshRequests();

                // Abrir o modal para visualizar o pedido criado
                const createdOrder = {
                    id: orderRef.id,
                    ...orderData
                };
                setViewingOrder(createdOrder);

                // Notificar sobre a conversão
                await notifyConversion(requestData, orderCode, quote.supplierName);

            } else {
                addToast('Erro ao criar pedido de compra', 'Error');
            }
        } catch (error) {
            console.error('Error converting quote to order:', error);
            addToast(`Erro ao converter cotação: ${error.message}`, 'Error');
        } finally {
            setFormLoading(false);
        }
    };

    // Função para notificar sobre conversão
    const notifyConversion = useCallback(async (request, orderCode, supplierName) => {
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));

            // Encontrar o criador da solicitação
            const creatorUser = usersSnapshot.docs.find(doc => {
                const data = doc.data();
                return data.nome === request.requestedBy || data.email === request.requestedBy;
            });

            const baseNotification = {
                type: 'purchase_converted',
                module: 'purchase',
                title: `📦 Cotação Convertida em Pedido`,
                fullMessage: `Detalhes:\n• Solicitação: ${request.code}\n• Fornecedor: ${supplierName}\n• Pedido: ${orderCode}\n• Convertido por: ${responsible}`,
                timestamp: new Date().toISOString(),
                read: false,
                priority: 'high',
                route: `/purchase-orders`,
                data: {
                    requestId: request.id,
                    requestCode: request.code,
                    orderCode: orderCode,
                    supplierName: supplierName
                }
            };

            // Notificar criador
            if (creatorUser && creatorUser.id !== currentUser?.uid) {
                addNotification({
                    ...baseNotification,
                    id: `purchase_converted_${request.id}_${creatorUser.id}_${Date.now()}`,
                    message: `Solicitação ${request.code} - Cotação do fornecedor ${supplierName} foi convertida em Pedido ${orderCode}`,
                    userId: creatorUser.id
                });
            }

            // Notificar Diretoria e Operacional
            const targetUsers = usersSnapshot.docs.filter(doc => {
                const data = doc.data();
                const roles = Array.isArray(data.role) ? data.role : [data.role];
                return roles.some(r => r === 'Diretoria' || r === 'Diretoria (Admin)' || r === 'Operacional');
            });

            for (const user of targetUsers) {
                if (user.id !== currentUser?.uid && user.id !== creatorUser?.id) {
                    addNotification({
                        ...baseNotification,
                        id: `purchase_converted_${request.id}_${user.id}_${Date.now()}`,
                        message: `Solicitação ${request.code} - Cotação do fornecedor ${supplierName} foi convertida em Pedido ${orderCode}`,
                        userId: user.id
                    });
                }
            }
        } catch (error) {
            console.error('Erro ao notificar conversão:', error);
        }
    }, [currentUser, responsible, addNotification]);

    const toggleSectionExpand = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    // ============================================
    // 3.10 CRUD HANDLERS
    // ============================================

    const handleNewClick = () => {
        resetForm();
        setIsModalOpen(true);
    };

    const handleEditClick = async (request) => {
        setEditingRequest(request);
        setRequestCode(request.code || '');
        setTitle(request.title || '');
        setDescription(request.description || '');
        setRequestType(request.requestType || REQUEST_TYPES[0].value);
        setPriority(request.priority || PRIORITY_OPTIONS[1].value);
        setRequestDate(request.requestDate || '');
        setRequiredDate(request.requiredDate || '');
        setDepartment(request.department || '');
        setRequestedBy(request.requestedBy || responsible);
        setRequestItems(request.items || []);
        setSupplierQuotes(request.supplierQuotes || []);
        setIsModalOpen(true);
    };

    const handleViewClick = (request) => {
        setViewingOrder(null);
        setViewingRequest(request);
    };

    const handlePrintClick = (request) => {
        setPrintModalData(request);
    };

    const handleViewConvertedOrder = async (requestId) => {
        const request = requests.find(r => r.id === requestId);
        if (!request) {
            addToast('Solicitação não encontrada.', 'Error');
            return;
        }

        try {
            const ordersSnapshot = await getDocs(collection(db, PURCHASE_ORDERS_COLLECTION));
            const orders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const ordersFromRequest = orders.filter(o => o.originalRequestId === requestId);

            if (ordersFromRequest.length > 0) {
                setViewingOrder(ordersFromRequest[0]);
                if (ordersFromRequest.length > 1) {
                    addToast(`Encontrados ${ordersFromRequest.length} pedidos para esta solicitação.`, 'Info');
                }
                setViewingRequest(null);
            } else {
                addToast('Pedido de compra não encontrado. Exibindo solicitação.', 'Info');
                setViewingOrder(null);
                setViewingRequest(request);
            }
        } catch (error) {
            console.error('Error fetching order:', error);
            addToast('Erro ao buscar pedido. Exibindo solicitação.', 'Error');
            setViewingOrder(null);
            setViewingRequest(request);
        }
    };

    const handleDelete = async (request) => {
        setDeleteConfirm({
            request: request,
            title: 'Confirmar Exclusão',
            message: `Tem certeza que deseja excluir a solicitação ${request.code}? Esta ação não pode ser desfeita.`,
            confirmText: 'Excluir',
            itemName: request.title || '',
            itemCode: request.code || '',
            icon: <FiTrash2 size={24} style={{ color: '#dc3545' }} />
        });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirm) return;
        const request = deleteConfirm.request;

        try {
            const result = await deleteDocument(request.id);
            if (result.success) {
                addToast('Solicitação excluída com sucesso!', 'Success');
                if (refreshRequests) await refreshRequests();
            } else {
                addToast(`Erro ao excluir: ${result.error}`, 'Error');
            }
        } catch (error) {
            addToast('Ocorreu um erro inesperado.', 'Error');
        } finally {
            setDeleteConfirm(null);
        }
    };

    // ============================================
    // 3.11 STATUS UPDATE HANDLERS
    // ============================================

    const handleUpdateStatus = async (requestId, newStatus, description = '') => {
        const request = requests.find(r => r.id === requestId);
        if (!request) return;

        setFormLoading(true);
        try {
            const existingHistory = request.trackingHistory || [];

            const statusLabel = REQUEST_STATUS_LABELS[newStatus]?.label || newStatus;
            const newHistoryEntry = {
                date: new Date().toLocaleDateString('pt-BR'),
                status: statusLabel,
                description: description || `Status alterado para ${statusLabel}`,
                updatedBy: responsible
            };

            const updateData = {
                status: newStatus,
                trackingHistory: [...existingHistory, newHistoryEntry],
                updatedAt: new Date(),
                ...(newStatus === REQUEST_STATUS.APPROVED && {
                    approvedAt: new Date(),
                    approvedBy: responsible
                }),
                ...(newStatus === REQUEST_STATUS.REJECTED && {
                    rejectedAt: new Date(),
                    rejectedBy: responsible
                })
            };

            const result = await updateDocument(requestId, updateData);
            if (result.success) {
                addToast(`Solicitação ${request.code} atualizada para "${statusLabel}"`, 'Success');
                await refreshRequests();

                if (newStatus === REQUEST_STATUS.APPROVED) {
                    await notifyStatusChange(request, 'aprovada', '✅');
                } else if (newStatus === REQUEST_STATUS.REJECTED) {
                    await notifyStatusChange(request, 'rejeitada', '❌');
                } else if (newStatus === REQUEST_STATUS.UNDER_REVIEW) {
                    await notifyStatusChange(request, 'em análise', '📋');
                }

                if (editingRequest && editingRequest.id === requestId) {
                    const updatedRequest = requests.find(r => r.id === requestId);
                    if (updatedRequest) {
                        setEditingRequest({
                            ...updatedRequest,
                            items: updatedRequest.items || []
                        });
                        setRequestItems(updatedRequest.items || []);
                        setSupplierQuotes(updatedRequest.supplierQuotes || []);
                        setRequestCode(updatedRequest.code || '');
                        setTitle(updatedRequest.title || '');
                        setDescription(updatedRequest.description || '');
                        setRequestType(updatedRequest.requestType || REQUEST_TYPES[0].value);
                        setPriority(updatedRequest.priority || PRIORITY_OPTIONS[1].value);
                        setRequestDate(updatedRequest.requestDate || '');
                        setRequiredDate(updatedRequest.requiredDate || '');
                        setDepartment(updatedRequest.department || '');
                        setRequestedBy(updatedRequest.requestedBy || responsible);
                    }
                }
                return true;
            } else {
                addToast(`Erro ao atualizar status: ${result.error}`, 'Error');
                return false;
            }
        } catch (error) {
            addToast(`Erro ao atualizar status: ${error.message}`, 'Error');
            return false;
        } finally {
            setFormLoading(false);
        }
    };

    // ============================================
    // 3.12 REVIEW & APPROVE HANDLERS
    // ============================================

    const handleOpenReview = async (request) => {
        setIsReviewModalOpen(false);

        if (request.status === REQUEST_STATUS.PENDING && can.approve) {
            setFormLoading(true);
            try {
                const updated = await handleUpdateStatus(request.id, REQUEST_STATUS.UNDER_REVIEW);
                if (updated) {
                    await refreshRequests();
                    const updatedRequest = requests.find(r => r.id === request.id);
                    if (updatedRequest) {
                        setEditingRequest(updatedRequest);
                        setRequestCode(updatedRequest.code || '');
                        setTitle(updatedRequest.title || '');
                        setDescription(updatedRequest.description || '');
                        setRequestType(updatedRequest.requestType || REQUEST_TYPES[0].value);
                        setPriority(updatedRequest.priority || PRIORITY_OPTIONS[1].value);
                        setRequestDate(updatedRequest.requestDate || '');
                        setRequiredDate(updatedRequest.requiredDate || '');
                        setDepartment(updatedRequest.department || '');
                        setRequestedBy(updatedRequest.requestedBy || responsible);
                        setRequestItems(updatedRequest.items || []);
                        setSupplierQuotes(updatedRequest.supplierQuotes || []);
                        setIsReviewModalOpen(true);
                    }
                } else {
                    setEditingRequest(request);
                    setRequestCode(request.code || '');
                    setTitle(request.title || '');
                    setDescription(request.description || '');
                    setRequestType(request.requestType || REQUEST_TYPES[0].value);
                    setPriority(request.priority || PRIORITY_OPTIONS[1].value);
                    setRequestDate(request.requestDate || '');
                    setRequiredDate(request.requiredDate || '');
                    setDepartment(request.department || '');
                    setRequestedBy(request.requestedBy || responsible);
                    setRequestItems(request.items || []);
                    setSupplierQuotes(request.supplierQuotes || []);
                    setIsReviewModalOpen(true);
                }
            } catch (error) {
                console.error('Error updating status:', error);
                setEditingRequest(request);
                setRequestCode(request.code || '');
                setTitle(request.title || '');
                setDescription(request.description || '');
                setRequestType(request.requestType || REQUEST_TYPES[0].value);
                setPriority(request.priority || PRIORITY_OPTIONS[1].value);
                setRequestDate(request.requestDate || '');
                setRequiredDate(request.requiredDate || '');
                setDepartment(request.department || '');
                setRequestedBy(request.requestedBy || responsible);
                setRequestItems(request.items || []);
                setSupplierQuotes(request.supplierQuotes || []);
                setIsReviewModalOpen(true);
            } finally {
                setFormLoading(false);
            }
        } else {
            setEditingRequest(request);
            setRequestCode(request.code || '');
            setTitle(request.title || '');
            setDescription(request.description || '');
            setRequestType(request.requestType || REQUEST_TYPES[0].value);
            setPriority(request.priority || PRIORITY_OPTIONS[1].value);
            setRequestDate(request.requestDate || '');
            setRequiredDate(request.requiredDate || '');
            setDepartment(request.department || '');
            setRequestedBy(request.requestedBy || responsible);
            setRequestItems(request.items || []);
            setSupplierQuotes(request.supplierQuotes || []);
            setIsReviewModalOpen(true);
        }
    };

    const handleSaveReview = async () => {
        if (!editingRequest) return;

        setFormLoading(true);
        setFormStatus({ message: '', type: '' });

        const updateData = {
            title: title.trim(),
            description: description.trim(),
            requestType,
            priority,
            requestDate,
            requiredDate,
            department: department.trim(),
            requestedBy,
            items: requestItems.map(item => ({
                ...item,
                quantity: parseFloat(item.quantity) || 0,
                unitPrice: parseFloat(item.unitPrice) || 0,
                total: parseFloat(item.total) || 0,
                suppliers: item.suppliers || []
            })),
            supplierQuotes: supplierQuotes.map(q => ({
                ...q,
                items: q.items || [],
                subtotal: parseFloat(q.subtotal) || 0,
                totalValue: parseFloat(q.totalValue) || 0,
                ipi: parseFloat(q.ipi) || 0,
                rejectionReason: q.rejectionReason || null,
                rejectedAt: q.rejectedAt || null,
                rejectedBy: q.rejectedBy || null,
                approvedAt: q.approvedAt || null,
                approvedBy: q.approvedBy || null,
                convertedAt: q.convertedAt || null,
                convertedToOrderId: q.convertedToOrderId || null,
                convertedToOrderCode: q.convertedToOrderCode || null
            })),
            totalValue: itemsSubtotal,
            updatedAt: new Date(),
            responsible: responsible
        };

        try {
            const docRef = doc(db, PURCHASE_REQUESTS_COLLECTION, editingRequest.id);
            await updateDoc(docRef, updateData);

            addToast('Solicitação atualizada com sucesso!', 'Success');
            setIsReviewModalOpen(false);
            if (refreshRequests) await refreshRequests();
            resetForm();
        } catch (error) {
            setFormStatus({ message: `Erro ao salvar: ${error.message}`, type: 'error' });
        } finally {
            setFormLoading(false);
        }
    };

    // ============================================
    // 3.13 FILTROS E PAGINAÇÃO
    // ============================================

    const filteredRequests = useMemo(() => {
        if (!requests) return [];

        let filtered = [...requests];

        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(req => {
                return (req.code && req.code.toLowerCase().includes(lowerSearch)) ||
                    (req.title && req.title.toLowerCase().includes(lowerSearch)) ||
                    (req.requestedBy && req.requestedBy.toLowerCase().includes(lowerSearch));
            });
        }

        if (statusFilter !== 'all') {
            filtered = filtered.filter(req => req.status === statusFilter);
        }

        return filtered.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
            return dateB - dateA;
        });
    }, [requests, searchTerm, statusFilter]);

    const totalPages = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE);
    const currentRequests = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredRequests.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredRequests, currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    // ============================================
    // 3.14 HELPER FUNCTIONS
    // ============================================

    const getPriorityIcon = (priority) => {
        const option = PRIORITY_OPTIONS.find(p => p.value === priority);
        return option ? option.icon : '⚪';
    };

    const getPriorityLabel = (priority) => {
        const option = PRIORITY_OPTIONS.find(p => p.value === priority);
        return option ? option.label : priority;
    };

    // ============================================
    // 3.15 RENDERIZAÇÃO DOS MODAIS
    // ============================================

    // MODAL 1: Create Request
    const renderCreateModal = () => {
        if (!isModalOpen) return null;

        return (
            <div className={styles.modalBackdrop}>
                <div className={styles.formContainer} style={{ maxWidth: '1200px' }}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h2>{editingRequest ? 'Editar Solicitação de Compra' : 'Nova Solicitação de Compra'}</h2>
                            <p className={styles.modalSubtitle}>
                                Código: <strong>{requestCode}</strong>
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={resetForm}
                            className={styles.modalCloseButton}
                            disabled={formLoading}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    {formStatus.message && (
                        <div className={`${styles.alert} ${styles[formStatus.type]}`}>
                            <span className={styles.alertIcon}>
                                {formStatus.type === 'error' && <FiAlertCircle size={18} />}
                                {formStatus.type === 'success' && <FiCheck size={18} />}
                            </span>
                            {formStatus.message}
                        </div>
                    )}

                    <div className={styles.formContent}>
                        {/* STEP 1: Items */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                                <h3 className={styles.sectionTitle}>Itens da Solicitação</h3>
                                <span className={styles.sectionBadge}>{requestItems.length} itens</span>
                            </div>
                            <div style={{ padding: '20px' }}>
                                <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                                    <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                        <label>Adicionar Matéria Prima / Produto</label>
                                        <ComboBox
                                            options={materialOptions}
                                            value={selectedMaterialId}
                                            onChange={setSelectedMaterialId}
                                            placeholder="Buscar produto..."
                                            disabled={formLoading}
                                            getOptionLabel={(opt) => opt.label}
                                            getOptionValue={(opt) => opt.id}
                                            getOptionKey={(opt) => opt.id}
                                        />
                                        {selectedMaterialId && (
                                            <small className={styles.fieldHint}>
                                                Fornecedores cadastrados: {
                                                    materialOptions.find(o => o.value === selectedMaterialId)?.suppliers?.length || 0
                                                }
                                            </small>
                                        )}
                                    </div>
                                    <div className={styles.inputGroup} style={{ justifyContent: 'flex-end', display: 'flex', alignItems: 'flex-end' }}>
                                        <button
                                            type="button"
                                            onClick={handleAddMaterial}
                                            className={styles.addButton}
                                            disabled={formLoading || !selectedMaterialId}
                                        >
                                            <FiPlus size={18} /> Adicionar Item
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.tableContainer}>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '30px' }}>#</th>
                                                <th style={{ width: '50px' }}>Código</th>
                                                <th>Produto</th>
                                                <th style={{ width: '140px' }}>Qtde</th>
                                                <th style={{ width: '120px' }}>Preço Estimado</th>
                                                <th style={{ width: '120px' }}>Total</th>
                                                <th style={{ width: '150px' }}>Fornecedores</th>
                                                <th style={{ width: '100px' }}>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {requestItems.length === 0 ? (
                                                <tr>
                                                    <td colSpan="8" className={styles.emptyTable}>
                                                        Nenhum item adicionado à solicitação.
                                                    </td>
                                                </tr>
                                            ) : (
                                                requestItems.map((item, index) => (
                                                    <tr key={item.id}>
                                                        <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                value={item.code || ''}
                                                                onChange={(e) => handleUpdateItem(item.id, 'code', e.target.value)}
                                                                className={styles.input}
                                                                disabled={formLoading}
                                                                placeholder="Código"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                value={item.name || ''}
                                                                onChange={(e) => handleUpdateItem(item.id, 'name', e.target.value)}
                                                                className={styles.input}
                                                                disabled={formLoading}
                                                                placeholder="Nome do produto"
                                                            />
                                                        </td>
                                                        <td style={{ width: '100px' }}>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={item.quantity || ''}
                                                                onChange={(e) => handleUpdateItem(item.id, 'quantity', e.target.value)}
                                                                className={styles.input}
                                                                disabled={formLoading}
                                                                placeholder="Qtde"
                                                            />
                                                        </td>
                                                        <td style={{ width: '120px' }}>
                                                            <input
                                                                type="number"
                                                                step="0.00001"
                                                                value={item.unitPrice || ''}
                                                                onChange={(e) => handleUpdateItem(item.id, 'unitPrice', e.target.value)}
                                                                className={styles.input}
                                                                disabled={formLoading}
                                                                placeholder="Preço estimado"
                                                            />
                                                        </td>
                                                        <td style={{ width: '120px' }}>
                                                            <input
                                                                type="text"
                                                                value={parseFloat(item.total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                                className={styles.input}
                                                                disabled
                                                                style={{ fontWeight: 'bold' }}
                                                            />
                                                        </td>
                                                        <td style={{ width: '150px' }}>
                                                            <small>
                                                                {(item.suppliers || []).map(s => s.razaoSocial).join(', ') || 'Nenhum'}
                                                            </small>
                                                        </td>
                                                        <td style={{ width: '100px', textAlign: 'center' }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveItem(item.id)}
                                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                                disabled={formLoading}
                                                                title="Remover item"
                                                            >
                                                                <FiTrash2 size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        {requestItems.length > 0 && (
                                            <tfoot>
                                                <tr className={styles.tableFooterRow}>
                                                    <td colSpan="5" className={styles.labelRight}>
                                                        <strong>Subtotal:</strong>
                                                    </td>
                                                    <td className={styles.valueCell}>
                                                        R$ {itemsSubtotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                    </td>
                                                    <td colSpan="2"></td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* STEP 2: Informações */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiInfo size={18} /></div>
                                <h3 className={styles.sectionTitle}>Informações da Solicitação</h3>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>Código</label>
                                    <input
                                        type="text"
                                        value={requestCode}
                                        className={styles.input}
                                        disabled
                                    />
                                    <small className={styles.fieldHint}>Formato: RC001+AA</small>
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>Data da Solicitação</label>
                                    <input
                                        type="text"
                                        value={requestDate}
                                        onChange={(e) => setRequestDate(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="dd/mm/aaaa"
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>Tipo de Solicitação</label>
                                    <select
                                        value={requestType}
                                        onChange={(e) => setRequestType(e.target.value)}
                                        className={styles.select}
                                        disabled={formLoading}
                                    >
                                        {REQUEST_TYPES.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>Prioridade</label>
                                    <select
                                        value={priority}
                                        onChange={(e) => setPriority(e.target.value)}
                                        className={styles.select}
                                        disabled={formLoading}
                                    >
                                        {PRIORITY_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.icon} {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>Solicitante</label>
                                    <input
                                        type="text"
                                        value={requestedBy}
                                        onChange={(e) => setRequestedBy(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="Nome do solicitante"
                                    />
                                </div>
                                <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                    <label>Descrição</label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        className={styles.textarea}
                                        rows={3}
                                        disabled={formLoading}
                                        placeholder="Descrição detalhada da solicitação..."
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.stepperActions}>
                            <button
                                type="button"
                                onClick={resetForm}
                                className={styles.secondaryButton}
                                disabled={formLoading}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleCreateRequest}
                                className={styles.primaryButton}
                                disabled={formLoading || requestItems.length === 0}
                            >
                                {formLoading ? (
                                    <>
                                        <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                        {editingRequest ? 'Atualizando...' : 'Criando...'}
                                    </>
                                ) : (
                                    <>
                                        {editingRequest ? <FiSave size={18} /> : <FiPlus size={18} />}
                                        {editingRequest ? ' Atualizar Solicitação' : ' Criar Solicitação'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // MODAL 2: Review & Quotes (Supplier-level)
    const renderReviewModal = () => {
        if (!isReviewModalOpen || !editingRequest) return null;

        const canApproveQuote = can.approve;
        const canConvertQuote = can.convert || can.approve;

        return (
            <div className={styles.modalBackdrop}>
                <div className={styles.formContainer} style={{ maxWidth: '1400px' }}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h2>
                                {editingRequest.status === REQUEST_STATUS.APPROVED
                                    ? `✅ Aprovado - ${editingRequest.code}`
                                    : editingRequest.status === REQUEST_STATUS.UNDER_REVIEW
                                        ? `📋 Em Análise - ${editingRequest.code}`
                                        : editingRequest.status === REQUEST_STATUS.REJECTED
                                            ? `❌ Rejeitado - ${editingRequest.code}`
                                            : editingRequest.status === REQUEST_STATUS.CONVERTED
                                                ? `🔄 Convertido - ${editingRequest.code}`
                                                : editingRequest.status === REQUEST_STATUS.PARTIALLY_CONVERTED
                                                    ? `🔄 Parcialmente Convertido - ${editingRequest.code}`
                                                    : `📝 Revisão - ${editingRequest.code}`}
                            </h2>
                            <p className={styles.modalSubtitle}>
                                Status atual: <StatusBadge status={editingRequest.status} />
                                {supplierQuotes.length > 0 && (
                                    <span style={{ marginLeft: '16px', fontSize: '14px' }}>
                                        Cotações: {
                                            supplierQuotes.filter(q => q.status === SUPPLIER_QUOTE_STATUS.APPROVED).length
                                        } aprovadas / {
                                            supplierQuotes.filter(q => q.status === SUPPLIER_QUOTE_STATUS.CONVERTED).length
                                        } convertidas / {supplierQuotes.length} total
                                    </span>
                                )}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={resetForm}
                            className={styles.modalCloseButton}
                            disabled={formLoading}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    {formStatus.message && (
                        <div className={`${styles.alert} ${styles[formStatus.type]}`}>
                            <span className={styles.alertIcon}>
                                {formStatus.type === 'error' && <FiAlertCircle size={18} />}
                                {formStatus.type === 'success' && <FiCheck size={18} />}
                            </span>
                            {formStatus.message}
                        </div>
                    )}

                    <div className={styles.formContent}>
                        {/* Informações Gerais */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiInfo size={18} /></div>
                                <h3 className={styles.sectionTitle}>Informações da Solicitação</h3>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>Código</label>
                                    <input type="text" value={requestCode} className={styles.input} disabled />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>Título <span className={styles.required}>*</span></label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="Título da solicitação"
                                        required
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>Tipo</label>
                                    <input
                                        type="text"
                                        value={REQUEST_TYPES.find(t => t.value === requestType)?.label || requestType}
                                        className={styles.input}
                                        disabled
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>Prioridade</label>
                                    <input
                                        type="text"
                                        value={`${getPriorityIcon(priority)} ${getPriorityLabel(priority)}`}
                                        className={styles.input}
                                        disabled
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>Data</label>
                                    <input type="text" value={requestDate} className={styles.input} disabled />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>Data Necessária</label>
                                    <input
                                        type="text"
                                        value={requiredDate}
                                        onChange={(e) => setRequiredDate(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="dd/mm/aaaa"
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>Departamento</label>
                                    <input
                                        type="text"
                                        value={department}
                                        onChange={(e) => setDepartment(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="Departamento"
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>Solicitante</label>
                                    <input
                                        type="text"
                                        value={requestedBy}
                                        onChange={(e) => setRequestedBy(e.target.value)}
                                        className={styles.input}
                                        disabled={formLoading}
                                        placeholder="Solicitante"
                                    />
                                </div>
                                <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                    <label>Descrição</label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        className={styles.textarea}
                                        rows={3}
                                        disabled={formLoading}
                                        placeholder="Descrição detalhada..."
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Itens da Solicitação (Read-only view) */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                                <h3 className={styles.sectionTitle}>Itens da Solicitação</h3>
                                <span className={styles.sectionBadge}>{requestItems.length} itens</span>
                            </div>
                            <div style={{ padding: '20px' }}>
                                <div className={styles.tableContainer}>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '50px' }}>#</th>
                                                <th>Código</th>
                                                <th>Produto</th>
                                                <th style={{ width: '100px' }}>Qtde</th>
                                                <th style={{ width: '120px' }}>Preço Unit.</th>
                                                <th style={{ width: '120px' }}>Total</th>
                                                <th style={{ width: '150px' }}>Fornecedores</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {requestItems.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                                    <td>{item.code}</td>
                                                    <td>{item.name}</td>
                                                    <td>{item.quantity}</td>
                                                    <td>{item.unitPrice ? `R$ ${parseFloat(item.unitPrice).toFixed(2)}` : '-'}</td>
                                                    <td style={{ fontWeight: 'bold' }}>
                                                        {item.total ? `R$ ${parseFloat(item.total).toFixed(2)}` : '-'}
                                                    </td>
                                                    <td>
                                                        <small>
                                                            {(item.suppliers || []).map(s => s.razaoSocial).join(', ') || 'Nenhum'}
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Cotações por Fornecedor - WITH INDIVIDUAL APPROVAL & CONVERSION */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FaQuoteRight size={18} /></div>
                                <h3 className={styles.sectionTitle}>Cotações por Fornecedor</h3>
                                <span className={styles.sectionBadge}>{supplierQuotes.length} cotações</span>
                            </div>
                            <div style={{ padding: '20px' }}>
                                <SupplierQuotesSection
                                    requestItems={requestItems}
                                    suppliers={suppliers}
                                    supplierQuotes={supplierQuotes}
                                    onAddQuote={handleAddSupplierQuote}
                                    onEditQuote={handleEditSupplierQuote}
                                    onDeleteQuote={handleDeleteSupplierQuote}
                                    onSelectQuote={handleSelectSupplierQuote}
                                    onApproveQuote={handleApproveSupplierQuote}
                                    onRejectQuote={handleRejectSupplierQuote}
                                    onOpenRejectModal={handleOpenRejectModal}
                                    onConvertQuote={handleConvertApprovedQuote}
                                    isEditing={formLoading}
                                    isExpanded={expandedSections['supplierQuotes'] || false}
                                    onToggleExpand={() => toggleSectionExpand('supplierQuotes')}
                                    canConvert={canConvertQuote}
                                    canApprove={canApproveQuote}
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className={styles.stepperActions}>
                            <div>
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className={styles.secondaryButton}
                                    disabled={formLoading}
                                >
                                    <FiX size={16} /> Fechar
                                </button>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    onClick={handleSaveReview}
                                    className={styles.primaryButton}
                                    disabled={formLoading}
                                >
                                    {formLoading ? (
                                        <>
                                            <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                            Salvando...
                                        </>
                                    ) : (
                                        <><FiSave size={18} /> Salvar Alterações</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================
    // 3.16 RENDERIZAÇÃO PRINCIPAL
    // ============================================

    return (
        <div className={styles.pageContainer}>
            {/* Toast Container */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer} aria-live="polite">
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            <DeleteModal
                isOpen={!!deleteConfirm}
                title={deleteConfirm?.title || 'Confirmar Exclusão'}
                message={deleteConfirm?.message || 'Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.'}
                itemName={deleteConfirm?.request?.title || ''}
                itemCode={deleteConfirm?.request?.code || ''}
                confirmText={deleteConfirm?.confirmText || 'Excluir'}
                cancelText="Cancelar"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteConfirm(null)}
                isLoading={formLoading}
                icon={deleteConfirm?.icon}
            />

            {/* Rejection Modal */}
            {rejectQuoteModal && (
                <div className={styles.modalBackdrop} style={{ zIndex: 10000 }}>
                    <div className={styles.formContainer} style={{ maxWidth: '500px' }}>
                        <div className={styles.modalHeader}>
                            <div>
                                <h2>Rejeitar Cotação</h2>
                                <p className={styles.modalSubtitle}>
                                    Fornecedor: <strong>{rejectQuoteModal.quote?.supplierName || 'N/A'}</strong>
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setRejectQuoteModal(null)}
                                className={styles.modalCloseButton}
                            >
                                <FiX size={24} />
                            </button>
                        </div>

                        <div className={styles.formContent}>
                            <div className={styles.formSection}>
                                <div className={styles.formGrid}>
                                    <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                        <label>
                                            Motivo da Rejeição <span className={styles.required}>*</span>
                                        </label>
                                        <textarea
                                            value={rejectQuoteModal.reason}
                                            onChange={(e) => setRejectQuoteModal(prev => ({
                                                ...prev,
                                                reason: e.target.value
                                            }))}
                                            className={styles.textarea}
                                            rows={4}
                                            placeholder="Descreva o motivo da rejeição desta cotação..."
                                            required
                                            autoFocus
                                        />
                                        <small className={styles.fieldHint}>
                                            Esta informação será registrada no histórico da solicitação.
                                        </small>
                                    </div>

                                    {rejectQuoteModal.quote?.items && rejectQuoteModal.quote.items.length > 0 && (
                                        <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                            <label>Itens da Cotação</label>
                                            <div className={styles.quoteItemsPreview}>
                                                {rejectQuoteModal.quote.items.map((item, idx) => (
                                                    <div key={idx} className={styles.quoteItemPreview}>
                                                        <span>{item.itemCode} - {item.itemName}</span>
                                                        <span>Qtde: {item.quantity}</span>
                                                        <span>
                                                            R$ {parseFloat(item.unitPrice || 0).toFixed(2)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className={styles.stepperActions}>
                                <button
                                    type="button"
                                    onClick={() => setRejectQuoteModal(null)}
                                    className={styles.secondaryButton}
                                    disabled={formLoading}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmReject}
                                    className={styles.dangerButton}
                                    disabled={formLoading || !rejectQuoteModal.reason.trim()}
                                >
                                    {formLoading ? (
                                        <>
                                            <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                            Rejeitando...
                                        </>
                                    ) : (
                                        <>
                                            <FiThumbsDown size={18} />
                                            Confirmar Rejeição
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Print Modal */}
            {printModalData && (
                <PrintModal
                    request={printModalData}
                    onClose={() => setPrintModalData(null)}
                    suppliers={suppliers}
                />
            )}

            {/* View Modals */}
            {viewingRequest && (
                <ViewOrderModal
                    order={viewingRequest}
                    onClose={() => setViewingRequest(null)}
                    customStatusLabels={REQUEST_STATUS_LABELS}
                />
            )}

            {viewingOrder && (
                <ViewOrderModal
                    order={viewingOrder}
                    onClose={() => setViewingOrder(null)}
                    customStatusLabels={REQUEST_STATUS_LABELS}
                />
            )}

            {renderCreateModal()}
            {renderReviewModal()}

            {/* Header */}
            <PageHeader
                title="Solicitações de Compra"
                actionText={can.create ? "Nova Solicitação" : ""}
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Filters */}
            <div className={styles.filterBar}>
                <div className={styles.filterSearchWrapper}>
                    <SearchBar
                        placeholder="Código, título, solicitante..."
                        onSearch={(value) => {
                            setSearchTerm(value);
                            setCurrentPage(1);
                        }}
                        initialValue={searchTerm}
                        debounceDelay={300}
                        disabled={formLoading}
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                    }}
                    className={styles.filterSelect}
                    disabled={formLoading}
                >
                    <option value="all">Todos os Status</option>
                    {Object.entries(REQUEST_STATUS_LABELS).map(([value, config]) => (
                        <option key={value} value={value}>{config.label}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Código</th>
                            <th>Tipo</th>
                            <th>Prioridade</th>
                            <th>Solicitante</th>
                            <th>Status</th>
                            <th style={{ width: '440px' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {showSkeleton ? (
                            <tr>
                                <td colSpan="8">
                                    <TableSkeleton columns={8} rows={ITEMS_PER_PAGE} />
                                </td>
                            </tr>
                        ) : currentRequests && currentRequests.length > 0 ? (
                            currentRequests.map(request => {
                                const isConverted = request.status === REQUEST_STATUS.CONVERTED;
                                const isPartiallyConverted = request.status === REQUEST_STATUS.PARTIALLY_CONVERTED;
                                const isPending = request.status === REQUEST_STATUS.PENDING;
                                const isUnderReview = request.status === REQUEST_STATUS.UNDER_REVIEW;
                                const canEdit = request.status !== REQUEST_STATUS.APPROVED &&
                                    request.status !== REQUEST_STATUS.CONVERTED &&
                                    request.status !== REQUEST_STATUS.REJECTED;
                                const canDelete = request.status !== REQUEST_STATUS.CONVERTED;
                                const canReview = (isPending || isUnderReview) && can.approve;

                                return (
                                    <tr key={request.id} className={styles.tableRow}>
                                        <td><strong>{request.code}</strong></td>
                                        <td>
                                            {REQUEST_TYPES.find(t => t.value === request.requestType)?.label || request.requestType}
                                        </td>
                                        <td>
                                            <span className={styles.priorityBadge}>
                                                {getPriorityIcon(request.priority)} {getPriorityLabel(request.priority)}
                                            </span>
                                        </td>
                                        <td>{request.requestedBy}</td>
                                        <td>
                                            <StatusBadge status={request.status} />
                                            {isPartiallyConverted && (
                                                <span className={styles.partialBadge}>
                                                    {(request.supplierQuotes || []).filter(q => q.status === SUPPLIER_QUOTE_STATUS.CONVERTED).length}/{(request.supplierQuotes || []).length}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <div className={styles.actionsContainer}>
                                                {can.view && (
                                                    <button
                                                        onClick={() => {
                                                            if (isConverted || isPartiallyConverted) {
                                                                handleViewConvertedOrder(request.id);
                                                            } else {
                                                                handleViewClick(request);
                                                            }
                                                        }}
                                                        className={`${styles.actionButton} ${styles.viewButton}`}
                                                        disabled={formLoading}
                                                        title={isConverted || isPartiallyConverted ? "Visualizar Pedidos" : "Visualizar Solicitação"}
                                                    >
                                                        <FiEye size={14} />
                                                    </button>
                                                )}

                                                {/* Print Button */}
                                                <button
                                                    onClick={() => handlePrintClick(request)}
                                                    className={`${styles.actionButton} ${styles.printButton}`}
                                                    disabled={formLoading}
                                                    title="Imprimir / PDF"
                                                >
                                                    <FiPrinter size={14} />
                                                </button>

                                                {canReview && (
                                                    <button
                                                        onClick={() => handleOpenReview(request)}
                                                        className={`${styles.actionButton} ${styles.reviewButton}`}
                                                        disabled={formLoading}
                                                        title={isPending ? "Iniciar Cotação" : "Gerenciar Cotações"}
                                                    >
                                                        <MdOutlineAttachMoney size={16} />
                                                    </button>
                                                )}

                                                {can.edit && canEdit && (
                                                    <button
                                                        onClick={() => handleEditClick(request)}
                                                        className={`${styles.actionButton} ${styles.editButton}`}
                                                        disabled={formLoading}
                                                        title="Editar"
                                                    >
                                                        <FiEdit size={14} />
                                                    </button>
                                                )}

                                                {can.delete && canDelete && (
                                                    <button
                                                        onClick={() => handleDelete(request)}
                                                        className={`${styles.actionButton} ${styles.deleteButton}`}
                                                        disabled={formLoading}
                                                        title="Excluir"
                                                    >
                                                        <FiTrash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="8">
                                    <EmptyState
                                        icon="📋"
                                        title={searchTerm ? 'Nenhuma solicitação encontrada.' : 'Nenhuma solicitação cadastrada.'}
                                        message={searchTerm ? 'Tente buscar por outro termo.' : 'Comece criando uma nova solicitação de compra.'}
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {!showSkeleton && totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredRequests.length}
                    onPageChange={handlePageChange}
                    disabled={formLoading}
                    itemsLabel="solicitações"
                />
            )}
        </div>
    );
};

export default PurchaseRequestPage;